import type {
  FetchStatementFundingListPayload,
  FetchStatementLeagueListPayload,
  FetchStatementMatchListPayload,
  FetchStatementNormalPayload,
  FetchStatementSummaryListPayload,
} from "../../api/statementApi";
import * as api from "../../api/statementApi";
import {
  AsianReportViewTypes as ViewTypes,
  AsianStatementTabTypes as TabTypes,
} from "../../utils/const";

const DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES = 1;

const StatementListModel = {
  state: {
    statementList: [] as Array<any>,
    statementTotal: 0,
    totalPages: 0,
    currentPage: 1,
    totalStake: 0,
    totalPayout: 0,
    totalTurnover: 0,
    totalCredit: 0,
    totalCreditForPage: 0,
    totalStakeForPage: 0,
    totalPayoutForPage: 0,
    openingBalance: {
      balance: 0,
      date: "",
    },
    closingBalance: {
      balance: 0,
      date: "",
    },
    reportListPerPage: 50,
    isReportListLoading: false,
    offset: 0,
    page: 0,
    isDetailedView: false,
    isSummaryView: true,
    currentView: ViewTypes.SUMMARY,
    defaultTab: TabTypes.BETTING,
  },
  reducers: {
    setStatementListRequest(state: Record<string, any>): any {
      return { ...state, isReportListLoading: true };
    },

    resetStatementList(state: Record<string, any>): any {
      return {
        ...state,
        statementList: [],
        statementTotal: 0,
        totalPages: 0,
        totalStake: 0,
        reportListPerPage: 50,
        isReportListLoading: false,
        offset: 0,
        page: 0,
      };
    },

    setStatementListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return {
        ...state,
        isReportListLoading: false,
        statementList: payload.statementList,
        hasReportListReachedEnd: payload.length === 0,
        offset: payload.offset + state.reportListPerPage,
        page: payload.page,
        totalStake: payload.totalStake,
        totalStakeForPage: payload.totalStakeForPage,
        totalPayout: payload.totalPayout,
        totalPayoutForPage: payload.totalPayoutForPage,
        statementTotal: payload.numberOfReports,
        totalPages: payload.totalPages,
        currentView: payload.currentView,
        isDetailedView: payload.isDetailedView,
        before: payload.before,
        after: payload.after,
      };
    },

    setStatementListExchangeBetSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return {
        ...state,
        isReportListLoading: false,
        statementList: payload.statementList,
        currentView: payload.currentView,
        backSubtotal: payload.backSubtotal,
        laySubtotal: payload.laySubtotal,
        marketSubtotal: payload.marketSubtotal,
        commissionRate: payload.commissionRate,
        commissionAmount: payload.commissionAmount,
        numberOfReports: payload.totalItems,
        totalItems: payload.totalItems,
        isDetailedView: true,
        marketId: payload.marketId,
        finalScores: payload.finalScores,
        totalPayout: payload.totalPayout,
      };
    },

    setFundingListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return {
        ...state,
        isReportListLoading: false,
        statementList: payload.statementList,
        hasReportListReachedEnd: payload.length === 0,
        offset: payload.offset + 25,
        page: payload.page,
        totalCredit: payload.totalCredit,
        totalCreditForPage: payload.totalCreditForPage,
        statementTotal: payload.numberOfReports,
        totalPages: payload.totalPages,
        currentView: payload.currentView,
        isDetailedView: payload.isDetailedView,
      };
    },

    setStatementSummaryListSuccess(
      state: Record<string, any>,
      payload: Record<string, any>
    ): any {
      return {
        ...state,
        isReportListLoading: false,
        statementList: payload.statementList,
        hasReportListReachedEnd: payload.statementList.length === 0,
        totalStake: payload.totalStake,
        totalTurnover: payload.totalTurnover,
        totalCredit: payload.totalCredit,
        stakeTotal: payload.stakeTotal,
        statementTotal: payload.numberOfReports,
        totalPages: payload.totalPages,
        currentView: payload.currentView,
        isDetailedView: false,
        closingBalance: payload.closingBalance,
        openingBalance: payload.openingBalance,
      };
    },

    setDefaultTab(state: Record<string, any>, payload: string): any {
      return { ...state, defaultTab: payload };
    },
  },
  effects: {
    async loadStatementSummaryList(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const params: FetchStatementSummaryListPayload = {
        tab: payload && payload.tab,
        page: payload && payload.page,
        size: payload && payload.size,
        sportId: payload && payload.sportId,
        after: payload && payload.after,
        before: payload && payload.before,
        filter: payload && payload.filter,
      };
      api.fetchStatementSummaryList(params).then(resp => {
        const statementSummaryList =
          resp.statementBetsByDateList ||
          resp.statements ||
          resp.statementTransactionsByDate ||
          resp.content ||
          resp.statementBets;
        const totalStake = resp && resp.stakeTotal;
        const totalTurnover = resp && resp.turnoverTotal;
        const totalCredit = resp && resp.creditTotal;
        const stakeTotal = resp && resp.stakeTotal;
        const numberOfReports = resp && resp.total;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        let closingBalance = {
          balance: 0,
          date: "",
        };
        let openingBalance = {
          balance: 0,
          date: "",
        };

        if (resp.closingBalance) {
          closingBalance = resp.closingBalance;
        }

        if (resp.openingBalance) {
          openingBalance = resp.openingBalance;
        }
        this.setStatementSummaryListSuccess({
          offset: params["offset"],
          statementList: statementSummaryList,
          totalStake: totalStake,
          totalTurnover: totalTurnover,
          totalCredit: totalCredit,
          stakeTotal: stakeTotal,
          numberOfReports: numberOfReports,
          currentView: "SUMMARY",
          closingBalance: closingBalance,
          openingBalance: openingBalance,
          totalPages: totalPages,
        });
      });
    },

    async loadStatementNormalList(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const params: FetchStatementNormalPayload = {
        tab: payload && payload.tab,
        page: payload && payload.page,
        size: payload && payload.size,
        participant: payload && payload.filter,
        before: payload && payload.before,
        after: payload && payload.after,
        runnerId: payload && payload.runnerId,
        leagueId: payload && payload.leagueId,
      };
      const isDetailedView =
        params["runnerId"] !== null ||
        params["leagueId"] !== null ||
        payload.isDetailedView;
      api.fetchStatementNormalList(params).then(resp => {
        const statementNormalList = resp.statementBets || resp.content;
        const totalStake = resp && resp.stakeTotal;
        const totalStakeForPage = resp && resp.stakePageTotal;
        const totalPayout = resp && resp.payoutTotal;
        const totalPayoutForPage = resp && resp.payoutPageTotal;
        const numberOfReports = resp && resp.totalElements;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setStatementListSuccess({
          offset: resp.offset,
          statementList: statementNormalList,
          totalStake: totalStake,
          totalStakeForPage: totalStakeForPage,
          totalPayout: totalPayout,
          totalPayoutForPage: totalPayoutForPage,
          numberOfReports: numberOfReports,
          currentView: "NORMAL",
          isDetailedView: isDetailedView,
          totalPages: totalPages,
        });
      });
    },

    async loadStatementLeagueList(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const params: FetchStatementLeagueListPayload = {
        tab: payload && payload.tab,
        page: payload && payload.page,
        size: payload && payload.size,
        competitionName: payload && payload.filter,
        before: payload && payload.before,
        after: payload && payload.after,
      };
      api.fetchStatementLeagueList(params).then(resp => {
        const statementLeagueList = resp.content;
        const numberOfReports = resp && resp.totalElements;
        const totalStake = resp && resp.stakeTotal;
        const totalPayout = resp && resp.payoutTotal;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setStatementListSuccess({
          statementList: statementLeagueList,
          totalStake: totalStake,
          totalPayout: totalPayout,
          numberOfReports: numberOfReports,
          currentView: "LEAGUE",
          isDetailedView: false,
          totalPages: totalPages,
        });
      });
    },

    async loadStatementMatchList(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const params: FetchStatementMatchListPayload = {
        tab: payload && payload.tab,
        page: payload && payload.page,
        size: payload && payload.size,
        before: payload && payload.before,
        after: payload && payload.after,
        eventName: payload && payload.filter,
      };
      api.fetchStatementMatchList(params).then(resp => {
        const statementMatchList = resp.content;
        const totalStake = resp.stakeTotal;
        const numberOfReports = resp && resp.total;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setStatementListSuccess({
          statementList: statementMatchList,
          totalStake: totalStake,
          totalStakeForPage: 0,
          numberOfReports: numberOfReports,
          currentView: "MATCH",
          isDetailedView: false,
          totalPages: totalPages,
        });
      });
    },

    async loadStatementFundingList(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const params: FetchStatementFundingListPayload = {
        tab: payload && payload.tab,
        page: payload && payload.page,
        size: payload && payload.size,
        before: payload && payload.before,
        after: payload && payload.after,
        participant: payload && payload.filter,
      };
      const isDetailedView = payload.isDetailedView;
      api.fetchStatementFundingList(params).then(resp => {
        const statementFundingList = resp.content;
        const totalCredit = resp && resp.creditTotal;
        const totalCreditForPage = resp && resp.creditTotalPage;
        const numberOfReports = resp && resp.totalElements;
        // FIXME: totalPages is not returned from the API
        const totalPages =
          resp.totalPages === undefined
            ? DEFAULT_NUMBER_OF_PAGES_WHEN_NO_PAGES
            : resp.totalPages;
        this.setFundingListSuccess({
          statementList: statementFundingList,
          totalCredit: totalCredit,
          totalCreditForPage: totalCreditForPage,
          numberOfReports: numberOfReports,
          currentView: "FUNDING",
          isDetailedView: isDetailedView,
          totalPages: totalPages,
        });
      });
    },

    async loadStatementExchangeMarkets(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const params = {};
      params["before"] = payload && payload.before;
      params["after"] = payload && payload.after;
      params["filter"] = payload && payload.filter;
      const isDetailedView = payload.isDetailedView;
      api.fetchStatementExchangeMarkets(params).then(resp => {
        this.setStatementListSuccess({
          statementList: resp.statementExchangeMarketSummaries,
          numberOfReports: resp.totalItems,
          currentView: "EXCHANGE_BETTING",
          totalPayout: resp.payoutTotal,
          isDetailedView: isDetailedView,
          before: payload.before,
          after: payload.after,
        });
      });
    },

    async loadStatementExchangeBetsPerMarket(payload: Record<string, any>) {
      this.resetStatementList();
      this.setStatementListRequest();
      const marketId = payload && payload.marketId;
      const filter = payload && payload.filter;
      api
        .fetchStatementExchangeBetsPerMarket({
          marketId: marketId,
          filter: filter,
        })
        .then(resp => {
          this.setStatementListExchangeBetSuccess({
            statementList: resp.statementExchangeBets,
            numberOfReports: resp.totalItems,
            currentView: "EXCHANGE_BETTING_BET",
            totalPayout: resp.payout,
            totalItems: resp.totalItems,
            backSubtotal: resp.backSubtotal,
            laySubtotal: resp.laySubtotal,
            marketSubtotal: resp.marketSubtotal,
            commissionRate: resp.commissionRate,
            commissionAmount: resp.commissionAmount,
            marketId: marketId,
            finalScores: resp.finalScores,
          });
        });
    },
  },
};
export default StatementListModel;
